/**
 * Quick-look slide-in for a single payment intent. Operators get an
 * at-a-glance summary without losing list context; "Open full page"
 * routes to the canonical detail page for the deep view.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { getPaymentIntent } from "@/lib/api";
import { formatMethod, formatMinorUnits } from "@/lib/format";
import { INTENT_STATE_BADGE_VARIANT, PROVIDER_TX_STATUS_VARIANT } from "@/lib/state-badges";

export function PaymentIntentSheet({
  id,
  open,
  onOpenChange,
}: {
  id: string | null;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}) {
  const query = useQuery({
    queryKey: ["payment-intent", id] as const,
    queryFn: () => getPaymentIntent(id ?? ""),
    enabled: Boolean(id) && open,
  });

  const intent = query.data;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full max-w-md sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Payment intent</SheetTitle>
          <SheetDescription>
            Quick look — full audit trail on the detail page.
          </SheetDescription>
        </SheetHeader>

        {query.isLoading || !intent ? (
          <div className="mt-6 space-y-4">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="mt-2 flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto pr-1">
            <div className="space-y-1">
              <div className="text-3xl font-semibold tabular-nums">
                {formatMinorUnits(intent.amount, intent.currency)}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <Badge variant={INTENT_STATE_BADGE_VARIANT[intent.state]} className="capitalize">
                  {intent.state.replace(/_/g, " ")}
                </Badge>
                <span className="text-muted-foreground">·</span>
                <span className="capitalize">{intent.direction}</span>
                <span className="text-muted-foreground">·</span>
                <span>{formatMethod(intent.method)}</span>
              </div>
            </div>

            <Separator />

            <dl className="grid grid-cols-1 gap-3 text-sm">
              <Field label="Intent ID" value={<code className="font-mono text-xs">{intent.id}</code>} />
              <Field
                label="Idempotency key"
                value={<code className="font-mono text-xs">{intent.idempotency_key || "—"}</code>}
              />
              <Field
                label="Tenant"
                value={
                  <>
                    {intent.tenant_name}
                    <Badge variant="muted" className="ml-2 capitalize">
                      {intent.tenant_environment}
                    </Badge>
                  </>
                }
              />
              <Field label="Created" value={new Date(intent.created_at).toLocaleString()} />
              <Field label="Updated" value={new Date(intent.updated_at).toLocaleString()} />
            </dl>

            {intent.provider_transaction ? (
              <div className="space-y-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Provider transaction
                </div>
                <dl className="grid grid-cols-1 gap-3 text-sm">
                  <Field
                    label="Status"
                    value={
                      <Badge
                        variant={PROVIDER_TX_STATUS_VARIANT[intent.provider_transaction.status]}
                        className="capitalize"
                      >
                        {intent.provider_transaction.status}
                      </Badge>
                    }
                  />
                  <Field label="Plugin" value={intent.provider_transaction.plugin} />
                  <Field
                    label="Provider ref"
                    value={
                      <code className="font-mono text-xs">
                        {intent.provider_transaction.provider_ref}
                      </code>
                    }
                  />
                  {intent.provider_transaction.error_code ? (
                    <Field
                      label="Error code"
                      value={
                        <span className="font-mono text-xs text-destructive">
                          {intent.provider_transaction.error_code}
                        </span>
                      }
                    />
                  ) : null}
                </dl>
              </div>
            ) : (
              <div className="rounded-md border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
                No provider transaction yet — the intent never reached dispatch.
              </div>
            )}

            {intent.events.length > 0 ? (
              <div className="space-y-2">
                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Last events
                </div>
                <ul className="space-y-1.5">
                  {intent.events.slice(0, 5).map((e) => (
                    <li
                      key={e.sequence}
                      className="flex items-center gap-2 text-xs"
                    >
                      <span className="w-8 shrink-0 text-right font-mono text-muted-foreground">
                        #{e.sequence}
                      </span>
                      <span className="flex-1 truncate font-medium">{e.event_type}</span>
                      <span className="shrink-0 text-muted-foreground">
                        {new Date(e.created_at).toLocaleTimeString()}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className="mt-auto flex gap-2 pt-4">
              <Button asChild className="flex-1">
                <Link to={`/payment-intents/${intent.id}`}>
                  Open full page
                  <ArrowUpRight />
                </Link>
              </Button>
              <Button variant="outline" size="icon" asChild aria-label="Open in new tab">
                <a href={`/payment-intents/${intent.id}`} target="_blank" rel="noreferrer">
                  <ExternalLink />
                </a>
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="min-w-0 max-w-[60%] text-right text-sm font-medium">{value}</dd>
    </div>
  );
}
